import Business from '@mui/icons-material/Business'
import Edit from '@mui/icons-material/Edit'
import Event from '@mui/icons-material/Event'
import History from '@mui/icons-material/History'
import LocalOffer from '@mui/icons-material/LocalOffer'
import Person from '@mui/icons-material/Person'
import Place from '@mui/icons-material/Place'
import Undo from '@mui/icons-material/Undo'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { displayNamesOfEventTypes } from 'src/consumer/constants'
import EventTypeCreationForm from 'src/mentor/components/MentorEventsPage/components/eventsListing/EventTypeCreationForm'
import EventTypeFormDrawer from 'src/mentor/components/MentorEventsPage/components/eventsListing/EventTypeFormDrawer'
import BaseAddCustomPriceComponent from 'src/mentor/components/MentorEventsPage/components/BaseAddCustomPriceComponent'
import {
  getClinicBrandIdFromClinicId,
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import RevertEventsDialog from 'src/supportPortal/components/common/mentor/eventTypes/RevertEventsDialog'
import {
  getSPDataWithParams,
  postSPDataWithParams,
} from 'src/supportPortal/services/services'
import {
  getAllOneOnOnePlansForSupportPortal,
  getMinimalMentorData,
} from 'src/supportPortal/views/therapists/util'
import { toast } from 'react-toastify'

const WORKSPACE_TABS = {
  overview: 'overview',
  events: 'events',
  packs: 'packs',
}

const FILTER_MIXED = '__mixed__'
const TAB_SEQUENCE = [
  {
    id: WORKSPACE_TABS.overview,
    label: 'Clinic Overview',
    description: 'Events, clinic appointments, and pack plans together for this clinic scope.',
  },
  {
    id: WORKSPACE_TABS.events,
    label: 'Events',
    description: 'Personal events for this clinic plus the clinic appointment types available here.',
  },
  {
    id: WORKSPACE_TABS.packs,
    label: 'Pack Plans',
    description: 'Bundle pack plans sold for this practitioner across clinic scopes. Filter by clinic when needed.',
  },
]

function titleCase(value) {
  return `${value || ''}`
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function formatEventTypeLabel(eventType) {
  return displayNamesOfEventTypes?.[eventType] || titleCase(eventType)
}

function formatModeLabel(mode) {
  if (!mode) {
    return 'Mode not set'
  }
  const normalizedMode = `${mode}`.trim().toLowerCase()
  if (normalizedMode === 'aumhum' || normalizedMode === 'online' || normalizedMode === 'video-call') {
    return 'Video Call'
  }
  if (normalizedMode === 'offline' || normalizedMode === 'in-person') {
    return 'In Clinic'
  }
  if (normalizedMode === 'home-visit' || normalizedMode === 'home_visit') {
    return 'Home Visit'
  }
  return titleCase(normalizedMode)
}

function buildClinicScopeLabel(clinicBrandName, clinicLocationName) {
  const brandLabel = clinicBrandName || 'Clinic brand'
  const locationLabel = clinicLocationName || 'Clinic location'
  return `${brandLabel} - ${locationLabel}`
}

function buildModeBadgeLabels(modeLabel) {
  if (!modeLabel || modeLabel === 'Mode not set') {
    return []
  }
  const normalizedLabel = `${modeLabel}`.toLowerCase()
  if (normalizedLabel.includes('video call') && normalizedLabel.includes('in clinic')) {
    return ['Mixed Mode']
  }
  return modeLabel
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
}

function formatDurationSummary(durationsInMinutes) {
  const uniqueDurations = [...new Set((durationsInMinutes || []).filter(Boolean))]
  if (uniqueDurations.length === 0) {
    return 'Not set'
  }
  return uniqueDurations
    .map((duration) => `${duration} min`)
    .join(', ')
}

function getPrimaryPricingDetailFromMatrix(pricingMatrix) {
  if (!pricingMatrix) {
    return null
  }
  if (pricingMatrix?.row) {
    return pricingMatrix.row
  }
  if (pricingMatrix?.ROW) {
    return pricingMatrix.ROW
  }
  const firstEntry = Object.values(pricingMatrix).find(Boolean)
  return firstEntry || null
}

function formatMoney(amount, currencySymbol, currencyId) {
  if (amount == null) {
    return 'Not set'
  }
  const prefix = currencySymbol || (currencyId ? currencyId.toUpperCase() : '')
  return `${prefix ? `${prefix} ` : ''}${amount}`
}

function buildPricingDisplay(pricingDetail, fallbackPlan = {}) {
  const basePrice = pricingDetail?.price ?? fallbackPlan?.price
  const discountedPrice =
    pricingDetail?.discountedPrice ?? fallbackPlan?.finalPrice ?? basePrice
  const introductoryPrice =
    pricingDetail?.introductoryPrice ?? fallbackPlan?.introductoryPrice
  const currencySymbol = pricingDetail?.currencySymbol ?? fallbackPlan?.currencySymbol
  const currencyId = pricingDetail?.currencyId ?? fallbackPlan?.currency

  if (basePrice == null && discountedPrice == null && introductoryPrice == null) {
    return {
      primary: 'Pricing not set',
      secondary: '',
    }
  }

  const secondaryParts = []
  if (basePrice != null && discountedPrice != null && basePrice !== discountedPrice) {
    secondaryParts.push(`Base ${formatMoney(basePrice, currencySymbol, currencyId)}`)
  }
  if (
    introductoryPrice != null &&
    discountedPrice != null &&
    introductoryPrice !== discountedPrice
  ) {
    secondaryParts.push(`Intro ${formatMoney(introductoryPrice, currencySymbol, currencyId)}`)
  }

  return {
    primary: formatMoney(discountedPrice ?? basePrice, currencySymbol, currencyId),
    secondary: secondaryParts.join(' • '),
  }
}

function buildEventPricingDisplay(linkedPlan) {
  if (!linkedPlan) {
    return {
      primary: 'Free or no linked pricing plan',
      secondary: '',
    }
  }
  return buildPricingDisplay(getPrimaryPricingDetailFromMatrix(linkedPlan?.pricingMatrix), linkedPlan)
}

function buildAppointmentPricingDisplay(appointmentType) {
  return buildPricingDisplay(appointmentType?.pricingDetail, {})
}

function buildBundleValidityLabel(bundlePack) {
  const bundleItemValidities = [...new Set(
    (bundlePack?.bundle || [])
      .map((bundleItem) => Number(bundleItem?.featureAndCreditValidityInDays || 0))
      .filter((validity) => validity > 0),
  )]

  if (bundleItemValidities.length === 1) {
    return `${bundleItemValidities[0]} days from purchase`
  }
  if (bundleItemValidities.length > 1) {
    return `${bundleItemValidities.join(', ')} days depending on item`
  }
  if (Number(bundlePack?.expiryInDays) > 0) {
    return `${bundlePack.expiryInDays} days from purchase`
  }
  return 'No expiry configured'
}

function buildAppointmentValidityLabel(appointmentType) {
  if (appointmentType?.itemsType === 'bundle') {
    const parts = []
    if (Number(appointmentType?.sessionCount) > 0) {
      parts.push(`${appointmentType.sessionCount} sessions`)
    }
    if (Number(appointmentType?.expiryInDays) > 0) {
      parts.push(`${appointmentType.expiryInDays} day validity`)
    }
    return parts.length > 0 ? parts.join(' • ') : 'Bundle configuration incomplete'
  }
  return 'Per booking'
}

function getBundleSourceChips(sourcePlan) {
  const sourceType = sourcePlan?.sourceType
  if (sourceType === 'mixed') {
    return ['Clinic appointment', 'Personal event for this clinic']
  }
  if (sourceType === 'clinic-appointment') {
    return ['Clinic appointment']
  }
  if (sourceType === 'practitioner-event') {
    return ['Personal event for this clinic']
  }
  return sourcePlan?.sourceSummary ? [sourcePlan.sourceSummary] : []
}

function buildBundleItemTitle(bundleItem, sourcePlan) {
  if (sourcePlan?.pricingPlanTitle) {
    return sourcePlan.pricingPlanTitle
  }
  if (sourcePlan?.title) {
    return sourcePlan.title
  }
  return bundleItem?.pricingPackId || 'Linked pricing plan'
}

function getValidWorkspaceTab(candidateTab, fallbackTab) {
  return Object.values(WORKSPACE_TABS).includes(candidateTab) ? candidateTab : fallbackTab
}

function buildFilterOptions(rows) {
  const seen = new Set()
  const options = []

  rows.forEach((row) => {
    ;(row?.eventTypes || []).forEach((eventType) => {
      if (!seen.has(eventType)) {
        seen.add(eventType)
        options.push({
          id: eventType,
          label: formatEventTypeLabel(eventType),
        })
      }
    })
  })

  options.sort((left, right) => left.label.localeCompare(right.label))
  return options
}

function matchesEventTypeFilters(row, filters) {
  if (!Array.isArray(filters) || filters.length === 0) {
    return true
  }
  const wantsMixedPlans = filters.includes(FILTER_MIXED)
  const selectedEventTypes = filters.filter((filterValue) => filterValue !== FILTER_MIXED)
  const rowHasSelectedType =
    selectedEventTypes.length > 0 &&
    (row?.eventTypes || []).some((eventType) => selectedEventTypes.includes(eventType))
  const rowMatchesMixedPlan =
    wantsMixedPlans && row?.rowKind === 'bundle-pack' && row?.isMixedEventType

  if (wantsMixedPlans && selectedEventTypes.length === 0) {
    return rowMatchesMixedPlan
  }
  if (!wantsMixedPlans && selectedEventTypes.length > 0) {
    return rowHasSelectedType
  }
  return rowHasSelectedType || rowMatchesMixedPlan
}

function RowKindChip({ rowKind }) {
  const configByRowKind = {
    event: { label: 'Personal Event', color: 'primary' },
    'clinic-appointment': { label: 'Clinic Appointment', color: 'warning' },
    'bundle-pack': { label: 'Pack Plan', color: 'secondary' },
  }
  const chipConfig = configByRowKind?.[rowKind] || {
    label: titleCase(rowKind),
    color: 'default',
  }

  return (
    <Chip
      size="small"
      color={chipConfig.color}
      variant="outlined"
      label={chipConfig.label}
      sx={{ width: 'fit-content' }}
    />
  )
}

function EventTypeFilterControls({
  filterOptions,
  selectedFilters,
  setSelectedFilters,
  showMixedFilter,
  isMobile,
}) {
  const mobileSelectedOptions = useMemo(
    () =>
      selectedFilters.map((filterValue) => {
        if (filterValue === FILTER_MIXED) {
          return { id: FILTER_MIXED, label: 'Mixed Plans' }
        }
        return (
          filterOptions.find((filterOption) => filterOption.id === filterValue) || {
            id: filterValue,
            label: formatEventTypeLabel(filterValue),
          }
        )
      }),
    [filterOptions, selectedFilters],
  )

  if (isMobile) {
    return (
      <FormControl fullWidth size="small">
        <InputLabel id="workspace-event-filter-label">Event Types</InputLabel>
        <Select
          labelId="workspace-event-filter-label"
          multiple
          value={selectedFilters}
          onChange={(event) => setSelectedFilters(event.target.value)}
          input={<OutlinedInput label="Event Types" />}
          renderValue={() => {
            if (mobileSelectedOptions.length === 0) {
              return 'All event types'
            }
            return (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {mobileSelectedOptions.map((option) => (
                  <Chip key={option.id} size="small" label={option.label} />
                ))}
              </Stack>
            )
          }}
        >
          {filterOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.label}
            </MenuItem>
          ))}
          {showMixedFilter ? <MenuItem value={FILTER_MIXED}>Mixed Plans</MenuItem> : null}
        </Select>
      </FormControl>
    )
  }

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Chip
        clickable
        color={selectedFilters.length === 0 ? 'primary' : 'default'}
        variant={selectedFilters.length === 0 ? 'filled' : 'outlined'}
        label="All"
        onClick={() => setSelectedFilters([])}
      />
      {filterOptions.map((option) => {
        const selected = selectedFilters.includes(option.id)
        return (
          <Chip
            key={option.id}
            clickable
            color={selected ? 'primary' : 'default'}
            variant={selected ? 'filled' : 'outlined'}
            label={option.label}
            onClick={() =>
              setSelectedFilters((prev) =>
                prev.includes(option.id)
                  ? prev.filter((value) => value !== option.id)
                  : [...prev, option.id],
              )
            }
          />
        )
      })}
      {showMixedFilter ? (
        <Chip
          clickable
          color={selectedFilters.includes(FILTER_MIXED) ? 'secondary' : 'default'}
          variant={selectedFilters.includes(FILTER_MIXED) ? 'filled' : 'outlined'}
          label="Mixed Plans"
          onClick={() =>
            setSelectedFilters((prev) =>
              prev.includes(FILTER_MIXED)
                ? prev.filter((value) => value !== FILTER_MIXED)
                : [...prev, FILTER_MIXED],
            )
          }
        />
      ) : null}
    </Stack>
  )
}

function DetailItemsCell({ detailItems = [] }) {
  if (!detailItems.length) {
    return <Typography variant="body2" color="text.secondary">No details</Typography>
  }

  return (
    <Stack spacing={1}>
      {detailItems.map((detailItem, index) => (
        <Box key={`${detailItem?.primary}-${index}`}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {detailItem?.primary}
          </Typography>
          {detailItem?.secondary ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
              {detailItem.secondary}
            </Typography>
          ) : null}
          {Array.isArray(detailItem?.chips) && detailItem.chips.length > 0 ? (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              {detailItem.chips.map((chipLabel) => (
                <Chip key={`${detailItem?.primary}-${chipLabel}`} size="small" variant="outlined" label={chipLabel} />
              ))}
            </Stack>
          ) : null}
        </Box>
      ))}
    </Stack>
  )
}

function WorkspaceRowsTable({ rows, onRowAction }) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" sx={{ minWidth: 1260 }}>
        <TableHead>
          <TableRow>
            <TableCell>Kind</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Clinic Brand + Location</TableCell>
            <TableCell>Event Types</TableCell>
            <TableCell>Content</TableCell>
            <TableCell>Duration &amp; Mode</TableCell>
            <TableCell>Pricing &amp; Validity</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.rowKind}-${row.id}`} hover>
              <TableCell sx={{ verticalAlign: 'top' }}>
                <Stack spacing={0.75}>
                  <RowKindChip rowKind={row.rowKind} />
                  {(row?.kindBadges || []).map((kindBadge) => (
                    <Chip
                      key={`${row.id}-${kindBadge}`}
                      size="small"
                      color={kindBadge === 'Mixed Mode' ? 'secondary' : 'default'}
                      variant="outlined"
                      label={kindBadge}
                      sx={{ width: 'fit-content' }}
                    />
                  ))}
                  {row?.isMixedEventType ? (
                    <Chip size="small" color="secondary" variant="outlined" label="Mixed plan" sx={{ width: 'fit-content' }} />
                  ) : null}
                </Stack>
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top', minWidth: 220 }}>
                <Typography sx={{ fontWeight: 700 }}>{row.title}</Typography>
                {row.subtitle ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {row.subtitle}
                  </Typography>
                ) : null}
                {row.scopeBadges?.length ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    {row.scopeBadges.map((badge) => (
                      <Chip key={`${row.id}-${badge}`} size="small" variant="outlined" label={badge} />
                    ))}
                  </Stack>
                ) : null}
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top', minWidth: 220 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {row.clinicScopeLabel || 'Not set'}
                </Typography>
                {row.clinicScopeSecondary ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
                    {row.clinicScopeSecondary}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top', minWidth: 160 }}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {(row.eventTypeTitles || []).map((eventTypeTitle) => (
                    <Chip
                      key={`${row.id}-${eventTypeTitle}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={eventTypeTitle}
                    />
                  ))}
                </Stack>
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top', minWidth: 280 }}>
                <DetailItemsCell detailItems={row.detailItems} />
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top', minWidth: 170 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {row.durationLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.modeLabel}
                </Typography>
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top', minWidth: 190 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {row.pricingPrimary}
                </Typography>
                {row.pricingSecondary ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
                    {row.pricingSecondary}
                  </Typography>
                ) : null}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  Validity: {row.validityLabel}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Edit fontSize="small" />}
                  onClick={() => onRowAction(row)}
                >
                  {row.actionLabel}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function WorkspaceRowsCards({ rows, onRowAction }) {
  return (
    <Stack spacing={2}>
      {rows.map((row) => (
        <Card key={`${row.rowKind}-${row.id}`} variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Stack spacing={0.75}>
                  <RowKindChip rowKind={row.rowKind} />
                  {(row?.kindBadges || []).map((kindBadge) => (
                    <Chip
                      key={`${row.id}-${kindBadge}`}
                      size="small"
                      color={kindBadge === 'Mixed Mode' ? 'secondary' : 'default'}
                      variant="outlined"
                      label={kindBadge}
                      sx={{ width: 'fit-content' }}
                    />
                  ))}
                  {row?.isMixedEventType ? (
                    <Chip size="small" color="secondary" variant="outlined" label="Mixed plan" sx={{ width: 'fit-content' }} />
                  ) : null}
                </Stack>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Edit fontSize="small" />}
                  onClick={() => onRowAction(row)}
                >
                  {row.actionLabel}
                </Button>
              </Stack>
              <Box>
                <Typography sx={{ fontWeight: 700 }}>{row.title}</Typography>
                {row.subtitle ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {row.subtitle}
                  </Typography>
                ) : null}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Clinic Brand + Location
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {row.clinicScopeLabel || 'Not set'}
                </Typography>
                {row.clinicScopeSecondary ? (
                  <Typography variant="caption" color="text.secondary">
                    {row.clinicScopeSecondary}
                  </Typography>
                ) : null}
              </Box>
              {row.scopeBadges?.length ? (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {row.scopeBadges.map((badge) => (
                    <Chip key={`${row.id}-${badge}`} size="small" variant="outlined" label={badge} />
                  ))}
                </Stack>
              ) : null}
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {(row.eventTypeTitles || []).map((eventTypeTitle) => (
                  <Chip
                    key={`${row.id}-${eventTypeTitle}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={eventTypeTitle}
                  />
                ))}
              </Stack>
              <DetailItemsCell detailItems={row.detailItems} />
              <Divider />
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Duration
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.durationLabel}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Mode
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.modeLabel}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Pricing
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {row.pricingPrimary}
                  </Typography>
                  {row.pricingSecondary ? (
                    <Typography variant="caption" color="text.secondary">
                      {row.pricingSecondary}
                    </Typography>
                  ) : null}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Validity
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.validityLabel}
                  </Typography>
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  )
}

export default function SupportPractitionerPlansWorkspacePage({ defaultTab = WORKSPACE_TABS.events }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { mentorSlug } = useParams()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [isLoadingContext, setIsLoadingContext] = useState(true)
  const [isLoadingWorkspaceData, setIsLoadingWorkspaceData] = useState(false)
  const [pageError, setPageError] = useState('')
  const [mentor, setMentor] = useState(null)
  const [associatedClinics, setAssociatedClinics] = useState([])
  const [selectedClinicId, setSelectedClinicId] = useState('')
  const [selectedClinicAppointmentTypes, setSelectedClinicAppointmentTypes] = useState([])
  const [sourcePlans, setSourcePlans] = useState([])
  const [bundlePacks, setBundlePacks] = useState([])
  const [oneOnOnePlans, setOneOnOnePlans] = useState([])
  const [selectedFilters, setSelectedFilters] = useState([])
  const [packClinicFilterId, setPackClinicFilterId] = useState('all')
  const [selectedEventType, setSelectedEventType] = useState(null)
  const [showEventTypeModal, setShowEventTypeModal] = useState(false)
  const [selectedBundlePack, setSelectedBundlePack] = useState(null)
  const [showBundleEditor, setShowBundleEditor] = useState(false)
  const [bundleEditorClinicId, setBundleEditorClinicId] = useState('')
  const [showBundleScopePicker, setShowBundleScopePicker] = useState(false)
  const [pendingBundleBrandId, setPendingBundleBrandId] = useState('')
  const [pendingBundleClinicId, setPendingBundleClinicId] = useState('')
  const [currentVersion, setCurrentVersion] = useState(null)
  const [originalEvents, setOriginalEvents] = useState([])
  const [showRevertDialog, setShowRevertDialog] = useState(false)
  const [isReverting, setIsReverting] = useState(false)

  const selectedClinicAssociation = useMemo(
    () => associatedClinics.find((clinic) => clinic?.id === selectedClinicId) || null,
    [associatedClinics, selectedClinicId],
  )
  const associatedClinicBrands = useMemo(
    () =>
      [
        ...new Map(
          associatedClinics.map((clinic) => [
            clinic?.clinicBrandId,
            {
              id: clinic?.clinicBrandId,
              title: clinic?.clinicBrandName || clinic?.clinicBrandSlug || 'Clinic brand',
            },
          ]),
        ).values(),
      ].filter((brand) => Boolean(brand?.id)),
    [associatedClinics],
  )
  const bundleEditorClinicAssociation = useMemo(
    () => associatedClinics.find((clinic) => clinic?.id === bundleEditorClinicId) || null,
    [associatedClinics, bundleEditorClinicId],
  )
  const pendingBundleBrandClinics = useMemo(
    () =>
      associatedClinics.filter((clinic) => {
        if (!pendingBundleBrandId) {
          return true
        }
        return clinic?.clinicBrandId === pendingBundleBrandId
      }),
    [associatedClinics, pendingBundleBrandId],
  )

  const canonicalBasePath = useMemo(
    () => `/support-home/practitioner-management/therapists/${mentorSlug}/events`,
    [mentorSlug],
  )

  const requestedClinicScope = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return {
      clinicBrandId: params.get('clinicBrandId') || params.get('clinic-brand-id') || '',
      clinicLocationId: params.get('clinicLocationId') || params.get('clinic-location-id') || '',
    }
  }, [location.search])

  const activeTab = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const requestedTab = params.get('tab')
    if (requestedTab) {
      return getValidWorkspaceTab(requestedTab, defaultTab)
    }
    if (location.pathname.endsWith('/bundle-packs')) {
      return WORKSPACE_TABS.packs
    }
    return defaultTab
  }, [defaultTab, location.pathname, location.search])

  const bundleEditorScopeRequestParams = useMemo(() => {
    if (!mentor?.id || !bundleEditorClinicAssociation?.id || !bundleEditorClinicAssociation?.clinicBrandId) {
      return null
    }
    return {
      scopeType: 'practitioner',
      'mentor-id': mentor.id,
      'clinic-brand-id': bundleEditorClinicAssociation.clinicBrandId,
      'clinic-location-id': bundleEditorClinicAssociation.id,
    }
  }, [bundleEditorClinicAssociation, mentor?.id])

  const selectedScopeSourcePlans = useMemo(
    () => sourcePlans.filter((sourcePlan) => sourcePlan?.clinicLocationId === selectedClinicAssociation?.id),
    [selectedClinicAssociation?.id, sourcePlans],
  )

  const bundleEditorScopeSourcePlans = useMemo(
    () => sourcePlans.filter((sourcePlan) => sourcePlan?.clinicLocationId === bundleEditorClinicAssociation?.id),
    [bundleEditorClinicAssociation?.id, sourcePlans],
  )

  const selectedScopeBundlePacks = useMemo(
    () => bundlePacks.filter((bundlePack) => bundlePack?.clinicLocationId === selectedClinicAssociation?.id),
    [bundlePacks, selectedClinicAssociation?.id],
  )

  const sourcePlansById = useMemo(
    () =>
      selectedScopeSourcePlans.reduce((acc, sourcePlan) => {
        acc[sourcePlan?.pricingPackId || sourcePlan?.id] = sourcePlan
        return acc
      }, {}),
    [selectedScopeSourcePlans],
  )

  const allSourcePlansById = useMemo(
    () =>
      sourcePlans.reduce((acc, sourcePlan) => {
        acc[sourcePlan?.pricingPackId || sourcePlan?.id] = sourcePlan
        return acc
      }, {}),
    [sourcePlans],
  )

  const oneOnOnePlansById = useMemo(
    () =>
      (oneOnOnePlans || []).reduce((acc, plan) => {
        acc[plan?.id] = plan
        return acc
      }, {}),
    [oneOnOnePlans],
  )

  const practitionerEventRows = useMemo(
    () =>
      (mentor?.eventTypes || [])
        .filter((eventType) => eventType?.contextId === selectedClinicAssociation?.id)
        .map((eventType) => {
          const linkedPlan = oneOnOnePlansById?.[eventType?.subscriptionPlanId]
          const pricingDisplay = buildEventPricingDisplay(linkedPlan)
          return {
            id: eventType?.uid || eventType?.id || eventType?.title,
            rowKind: 'event',
            title: eventType?.title || 'Untitled event',
            subtitle: 'Personal event for this clinic',
            eventTypes: [eventType?.type].filter(Boolean),
            eventTypeTitles: [formatEventTypeLabel(eventType?.type)].filter(Boolean),
            scopeBadges: [
              'Personal event',
              selectedClinicAssociation?.title ? `Clinic: ${selectedClinicAssociation.title}` : null,
            ].filter(Boolean),
            clinicScopeLabel: buildClinicScopeLabel(
              selectedClinicAssociation?.clinicBrandName || selectedClinicAssociation?.clinicBrandSlug,
              selectedClinicAssociation?.title || selectedClinicAssociation?.name,
            ),
            clinicLocationId: selectedClinicAssociation?.id,
            clinicBrandId: selectedClinicAssociation?.clinicBrandId,
            kindBadges: buildModeBadgeLabels(formatModeLabel(eventType?.meetingSrc)),
            detailItems: [
              {
                primary: linkedPlan?.title || 'No linked pricing plan',
                secondary: linkedPlan
                  ? `Pricing plan ID: ${linkedPlan?.id}`
                  : eventType?.free
                    ? 'This event is free'
                    : 'Attach a pricing plan while editing',
                chips: [
                  eventType?.enabled ? 'Enabled' : 'Disabled',
                  eventType?.publicEvent ? 'Public' : 'Private',
                ],
              },
              eventType?.bundlePlanIds?.length
                ? {
                    primary: `${eventType.bundlePlanIds.length} linked bundle pack${eventType.bundlePlanIds.length > 1 ? 's' : ''}`,
                    secondary: 'This event can be redeemed from the linked pack plans.',
                    chips: ['Pack-enabled event'],
                  }
                : null,
            ].filter(Boolean),
            durationLabel: formatDurationSummary([eventType?.durationInMins]),
            modeLabel: formatModeLabel(eventType?.meetingSrc),
            pricingPrimary: pricingDisplay.primary,
            pricingSecondary: pricingDisplay.secondary,
            validityLabel: 'Single booking',
            actionLabel: 'Edit Event',
            raw: eventType,
          }
        })
        .sort((leftRow, rightRow) => leftRow.title.localeCompare(rightRow.title)),
    [mentor?.eventTypes, oneOnOnePlansById, selectedClinicAssociation?.id, selectedClinicAssociation?.title],
  )

  const clinicAppointmentRows = useMemo(
    () =>
      (selectedClinicAppointmentTypes || [])
        .map((appointmentType) => {
          const pricingDisplay = buildAppointmentPricingDisplay(appointmentType)
          return {
            id: appointmentType?.id || appointmentType?.title,
            rowKind: 'clinic-appointment',
            title: appointmentType?.title || 'Untitled clinic appointment',
            subtitle: 'Clinic appointment type available in this clinic',
            eventTypes: [appointmentType?.type].filter(Boolean),
            eventTypeTitles: [formatEventTypeLabel(appointmentType?.type)].filter(Boolean),
            scopeBadges: [
              'Clinic appointment',
              appointmentType?.itemsType === 'bundle' ? 'Bundle appointment' : 'Single appointment',
            ].filter(Boolean),
            clinicScopeLabel: buildClinicScopeLabel(
              selectedClinicAssociation?.clinicBrandName || selectedClinicAssociation?.clinicBrandSlug,
              selectedClinicAssociation?.title || selectedClinicAssociation?.name,
            ),
            clinicLocationId: selectedClinicAssociation?.id,
            clinicBrandId: selectedClinicAssociation?.clinicBrandId,
            kindBadges: buildModeBadgeLabels(
              formatModeLabel(
                appointmentType?.mode || appointmentType?.visitEventType || appointmentType?.meetingSrc,
              ),
            ),
            detailItems: [
              {
                primary:
                  appointmentType?.itemsType === 'bundle'
                    ? 'Bookable bundle appointment'
                    : 'Standard clinic appointment',
                secondary:
                  appointmentType?.itemsType === 'bundle'
                    ? buildAppointmentValidityLabel(appointmentType)
                    : 'Managed from clinic services',
                chips: [appointmentType?.mode ? formatModeLabel(appointmentType.mode) : null].filter(Boolean),
              },
            ],
            durationLabel: formatDurationSummary([
              appointmentType?.duration || appointmentType?.durationInMins,
            ]),
            modeLabel: formatModeLabel(
              appointmentType?.mode || appointmentType?.visitEventType || appointmentType?.meetingSrc,
            ),
            pricingPrimary: pricingDisplay.primary,
            pricingSecondary: pricingDisplay.secondary,
            validityLabel: buildAppointmentValidityLabel(appointmentType),
            actionLabel: 'Open Service',
            raw: appointmentType,
          }
        })
        .sort((leftRow, rightRow) => leftRow.title.localeCompare(rightRow.title)),
    [selectedClinicAppointmentTypes],
  )

  const buildBundlePackRows = useCallback(
    (packsToMap, sourcePlansMap, options = {}) =>
      (packsToMap || [])
        .map((bundlePack) => {
          const bundleDetails = (bundlePack?.bundle || []).map((bundleItem) => {
            const sourcePlan = sourcePlansMap?.[bundleItem?.pricingPackId]
            const bundleSourceChips = [
              ...getBundleSourceChips(sourcePlan),
              Number(bundleItem?.maxCount) > 0 ? `${bundleItem.maxCount} sessions` : null,
            ].filter(Boolean)

            return {
              primary: buildBundleItemTitle(bundleItem, sourcePlan),
              secondary: [
                sourcePlan?.sourceTitles?.length
                  ? `Appointment titles: ${sourcePlan.sourceTitles.join(', ')}`
                  : null,
                sourcePlan?.durationsInMinutes?.length
                  ? `${formatDurationSummary(sourcePlan.durationsInMinutes)} • ${(
                      sourcePlan?.deliveryModes || []
                    )
                      .map(formatModeLabel)
                      .filter(Boolean)
                      .join(', ')}`
                  : null,
              ]
                .filter(Boolean)
                .join(' • '),
              chips: bundleSourceChips,
            }
          })

          const aggregatedEventTypes = [
            ...new Set(
              bundleDetails.flatMap((detailItem, index) => {
                const sourcePlan = sourcePlansMap?.[bundlePack?.bundle?.[index]?.pricingPackId]
                return sourcePlan?.eventTypes || []
              }),
            ),
          ]
          const aggregatedEventTypeTitles = aggregatedEventTypes.map(formatEventTypeLabel)
          const aggregatedDurations = [
            ...new Set(
              (bundlePack?.bundle || []).flatMap((bundleItem) => {
                const sourcePlan = sourcePlansMap?.[bundleItem?.pricingPackId]
                return sourcePlan?.durationsInMinutes || []
              }),
            ),
          ]
          const aggregatedModes = [
            ...new Set(
              (bundlePack?.bundle || []).flatMap((bundleItem) => {
                const sourcePlan = sourcePlansMap?.[bundleItem?.pricingPackId]
                return (sourcePlan?.deliveryModes || []).map(formatModeLabel)
              }),
            ),
          ]
          const modeLabel = aggregatedModes.length > 0 ? aggregatedModes.join(', ') : 'Mode not set'
          const pricingDisplay = buildPricingDisplay(
            getPrimaryPricingDetailFromMatrix(bundlePack?.pricingMatrix),
            bundlePack,
          )
          const hasClinicAppointment = bundleDetails.some((detailItem) =>
            detailItem?.chips?.includes('Clinic appointment'),
          )
          const hasPractitionerEvent = bundleDetails.some((detailItem) =>
            detailItem?.chips?.includes('Personal event for this clinic'),
          )

          return {
            id: bundlePack?.id || bundlePack?.title,
            rowKind: 'bundle-pack',
            title: bundlePack?.title || 'Untitled pack plan',
            subtitle:
              options?.includeClinicScopeInSubtitle
                ? 'Bundle pack plan sold for this practitioner'
                : 'Bundle pack plan for this clinic scope',
            eventTypes: aggregatedEventTypes,
            eventTypeTitles: aggregatedEventTypeTitles,
            isMixedEventType: aggregatedEventTypes.length > 1,
            scopeBadges: [
              hasClinicAppointment ? 'Includes clinic appointment' : null,
              hasPractitionerEvent ? 'Includes personal event for this clinic' : null,
            ].filter(Boolean),
            clinicScopeLabel: buildClinicScopeLabel(
              bundlePack?.clinicBrandName,
              bundlePack?.clinicLocationName,
            ),
            clinicScopeSecondary: bundlePack?.clinicLocationId || bundlePack?.clinicBrandId ? null : 'Clinic scope unavailable',
            clinicLocationId: bundlePack?.clinicLocationId,
            clinicBrandId: bundlePack?.clinicBrandId,
            kindBadges: buildModeBadgeLabels(modeLabel),
            detailItems: bundleDetails,
            durationLabel: formatDurationSummary(aggregatedDurations),
            modeLabel,
            pricingPrimary: pricingDisplay.primary,
            pricingSecondary: pricingDisplay.secondary,
            validityLabel: buildBundleValidityLabel(bundlePack),
            actionLabel: 'Edit Pack',
            raw: bundlePack,
          }
        })
        .sort((leftRow, rightRow) => leftRow.title.localeCompare(rightRow.title)),
    [],
  )

  const selectedClinicBundlePackRows = useMemo(
    () => buildBundlePackRows(selectedScopeBundlePacks, sourcePlansById),
    [buildBundlePackRows, selectedScopeBundlePacks, sourcePlansById],
  )

  const allBundlePackRows = useMemo(
    () =>
      buildBundlePackRows(bundlePacks, allSourcePlansById, {
        includeClinicScopeInSubtitle: true,
      }),
    [allSourcePlansById, buildBundlePackRows, bundlePacks],
  )

  const overviewRows = useMemo(
    () =>
      [...practitionerEventRows, ...clinicAppointmentRows, ...selectedClinicBundlePackRows].sort((leftRow, rightRow) => {
        const rowOrder = {
          event: 0,
          'clinic-appointment': 1,
          'bundle-pack': 2,
        }
        const leftOrder = rowOrder?.[leftRow?.rowKind] ?? 99
        const rightOrder = rowOrder?.[rightRow?.rowKind] ?? 99
        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder
        }
        return leftRow.title.localeCompare(rightRow.title)
      }),
    [clinicAppointmentRows, practitionerEventRows, selectedClinicBundlePackRows],
  )

  const packTabRows = useMemo(() => {
    if (packClinicFilterId === 'all') {
      return allBundlePackRows
    }
    return allBundlePackRows.filter((row) => row?.clinicLocationId === packClinicFilterId)
  }, [allBundlePackRows, packClinicFilterId])

  const filterOptions = useMemo(() => {
    if (activeTab === WORKSPACE_TABS.packs) {
      return buildFilterOptions(packTabRows)
    }
    return buildFilterOptions(overviewRows)
  }, [activeTab, overviewRows, packTabRows])

  const visibleRows = useMemo(() => {
    const rowsByTab = {
      [WORKSPACE_TABS.overview]: overviewRows,
      [WORKSPACE_TABS.events]: [...practitionerEventRows, ...clinicAppointmentRows],
      [WORKSPACE_TABS.packs]: packTabRows,
    }
    return (rowsByTab?.[activeTab] || []).filter((row) => matchesEventTypeFilters(row, selectedFilters))
  }, [activeTab, clinicAppointmentRows, overviewRows, packTabRows, practitionerEventRows, selectedFilters])

  const showMixedFilter = useMemo(
    () =>
      (activeTab === WORKSPACE_TABS.packs ? packTabRows : selectedClinicBundlePackRows).some(
        (bundlePackRow) => bundlePackRow?.isMixedEventType,
      ),
    [activeTab, packTabRows, selectedClinicBundlePackRows],
  )

  const currentTabConfig = useMemo(
    () => TAB_SEQUENCE.find((tabConfig) => tabConfig.id === activeTab) || TAB_SEQUENCE[0],
    [activeTab],
  )

  const pageHeading = useMemo(
    () => `${mentor?.name || 'Practitioner'} Events & Plans`,
    [mentor?.name],
  )

  const pageSubHeading = useMemo(() => {
    if (!selectedClinicAssociation) {
      return 'Select a clinic scope to manage personal events, clinic appointments, and pack plans together.'
    }
    return `Managing ${selectedClinicAssociation?.clinicBrandName || 'Clinic brand'} - ${
      selectedClinicAssociation?.title || 'Clinic'
    } in one workspace.`
  }, [selectedClinicAssociation])

  const loadContext = useCallback(async () => {
    setIsLoadingContext(true)
    setPageError('')
    try {
      const [mentorData, clinicsResp] = await Promise.all([
        getMinimalMentorData(mentorSlug, true),
        getSPDataWithParams(
          '/api/secure/cc/support/therapists/clinic-associations',
          { 'mentor-slug': mentorSlug },
          undefined,
          undefined,
          true,
        ),
      ])

      if (!mentorData) {
        navigate('/support-home/practitioner-management/therapists')
        return
      }

      setMentor(mentorData)
      setCurrentVersion(mentorData?.currentVersion ?? mentorData?.version ?? null)
      setOriginalEvents(Array.isArray(mentorData?.eventTypes) ? [...mentorData.eventTypes] : [])

      if (isCommonResponseSuccessfulV2(clinicsResp)) {
        const clinics = getDataMapFromCommonResponse(clinicsResp)?.clinic || []
        setAssociatedClinics(clinics)

        const storedClinicString = sessionStorage.getItem('supportClinicData')
        let defaultClinicId =
          clinics.find((clinic) => clinic?.id === requestedClinicScope?.clinicLocationId)?.id ||
          clinics.find((clinic) => clinic?.clinicBrandId === requestedClinicScope?.clinicBrandId)?.id ||
          ''
        if (!defaultClinicId && storedClinicString) {
          try {
            const storedClinic = JSON.parse(storedClinicString)
            if (clinics.some((clinic) => clinic?.id === storedClinic?.id)) {
              defaultClinicId = storedClinic.id
            }
          } catch (error) {
            // ignore invalid session data
          }
        }
        if (!defaultClinicId) {
          defaultClinicId = clinics?.[0]?.id || ''
        }
        setSelectedClinicId(defaultClinicId)
      } else {
        setAssociatedClinics([])
        setSelectedClinicId('')
      }
    } catch (error) {
      setPageError('Unable to load this practitioner workspace right now.')
    } finally {
      setIsLoadingContext(false)
    }
  }, [mentorSlug, navigate, requestedClinicScope?.clinicBrandId, requestedClinicScope?.clinicLocationId])

  const loadWorkspaceData = useCallback(async () => {
    if (!mentor?.id || associatedClinics.length === 0) {
      setSourcePlans([])
      setBundlePacks([])
      setSelectedClinicAppointmentTypes([])
      setOneOnOnePlans([])
      return
    }

    setIsLoadingWorkspaceData(true)
    setPageError('')
    try {
      const scopedBundleRequests = associatedClinics.map(async (clinic) => {
        const clinicScopeParams = {
          scopeType: 'practitioner',
          'mentor-id': mentor.id,
          'clinic-brand-id': clinic?.clinicBrandId,
          'clinic-location-id': clinic?.id,
        }
        const [sourcePlansResp, bundlePacksResp] = await Promise.all([
          getSPDataWithParams('/api/secure/cc/support/bundle-pack/source-plans', clinicScopeParams),
          getSPDataWithParams('/api/secure/cc/support/bundle-pack/list', clinicScopeParams),
        ])

        return {
          clinic,
          sourcePlansResp,
          bundlePacksResp,
        }
      })

      const [scopedBundleResponses, clinicAppointmentsResp, oneOnOnePlansResp] =
        await Promise.all([
          Promise.all(scopedBundleRequests),
          selectedClinicAssociation
            ? getSPDataWithParams(
                '/api/secure/cc/support/clinic/clinic-appointment-type/list',
                {
                  clinicLocationId: selectedClinicAssociation?.id,
                  clinicBrandId: selectedClinicAssociation?.clinicBrandId,
                  mentorId: mentor?.id,
                },
                undefined,
                undefined,
                true,
              )
            : Promise.resolve(null),
          getAllOneOnOnePlansForSupportPortal(mentorSlug),
        ])

      const nextSourcePlans = []
      const nextBundlePacks = []
      let hasBundleScopeError = false

      scopedBundleResponses.forEach(({ clinic, sourcePlansResp, bundlePacksResp }) => {
        if (!isCommonResponseSuccessfulV2(sourcePlansResp) || !isCommonResponseSuccessfulV2(bundlePacksResp)) {
          hasBundleScopeError = true
          return
        }
        const scopedSourcePlans = getDataMapFromCommonResponse(sourcePlansResp)?.sourcePlans || []
        const scopedBundlePacks = getDataMapFromCommonResponse(bundlePacksResp)?.['plan-bundle-pack'] || []

        scopedSourcePlans.forEach((sourcePlan) => {
          nextSourcePlans.push({
            ...sourcePlan,
            clinicLocationId: clinic?.id,
            clinicBrandId: clinic?.clinicBrandId,
            clinicLocationName: clinic?.title || clinic?.name,
            clinicBrandName: clinic?.clinicBrandName || clinic?.clinicBrandSlug,
          })
        })
        scopedBundlePacks.forEach((bundlePack) => {
          nextBundlePacks.push({
            ...bundlePack,
            clinicLocationId: clinic?.id,
            clinicBrandId: clinic?.clinicBrandId,
            clinicLocationName: clinic?.title || clinic?.name,
            clinicBrandName: clinic?.clinicBrandName || clinic?.clinicBrandSlug,
          })
        })
      })

      setSourcePlans(nextSourcePlans)
      setBundlePacks(nextBundlePacks)

      if (hasBundleScopeError) {
        setPageError('Some clinic pack plans could not be loaded right now. Showing the scopes that were available.')
      }

      if (clinicAppointmentsResp && isCommonResponseSuccessfulV2(clinicAppointmentsResp)) {
        setSelectedClinicAppointmentTypes(
          getDataMapFromCommonResponse(clinicAppointmentsResp)?.appointmentTypes || [],
        )
      } else {
        setSelectedClinicAppointmentTypes([])
      }

      setOneOnOnePlans(Array.isArray(oneOnOnePlansResp) ? oneOnOnePlansResp : [])
    } catch (error) {
      setPageError('Unable to load the selected clinic scope right now.')
    } finally {
      setIsLoadingWorkspaceData(false)
    }
  }, [associatedClinics, mentor?.id, mentorSlug, selectedClinicAssociation, selectedClinicAssociation?.clinicBrandId, selectedClinicAssociation?.id])

  useEffect(() => {
    loadContext()
  }, [loadContext])

  useEffect(() => {
    if (selectedClinicAssociation) {
      sessionStorage.setItem('supportClinicData', JSON.stringify(selectedClinicAssociation))
    }
  }, [selectedClinicAssociation])

  useEffect(() => {
    setSelectedFilters([])
  }, [selectedClinicId])

  useEffect(() => {
    if (packClinicFilterId === 'all') {
      return
    }
    if (!associatedClinics.some((clinic) => clinic?.id === packClinicFilterId)) {
      setPackClinicFilterId('all')
    }
  }, [associatedClinics, packClinicFilterId])

  useEffect(() => {
    if (!showBundleScopePicker) {
      return
    }
    if (!pendingBundleBrandId) {
      if (associatedClinicBrands.length === 1) {
        setPendingBundleBrandId(associatedClinicBrands[0].id)
      }
      return
    }
    const locationStillMatchesBrand = pendingBundleBrandClinics.some(
      (clinic) => clinic?.id === pendingBundleClinicId,
    )
    if (!locationStillMatchesBrand) {
      setPendingBundleClinicId(pendingBundleBrandClinics?.[0]?.id || '')
    }
  }, [
    associatedClinicBrands,
    pendingBundleBrandClinics,
    pendingBundleBrandId,
    pendingBundleClinicId,
    showBundleScopePicker,
  ])

  useEffect(() => {
    loadWorkspaceData()
  }, [loadWorkspaceData])

  async function saveEventTypes(events, note = '') {
    const originalEventUids = new Set(originalEvents.map((eventType) => eventType?.uid).filter(Boolean))
    const currentEventUids = new Set(events.map((eventType) => eventType?.uid).filter(Boolean))
    const deleteEventUids = Array.from(originalEventUids).filter((uid) => !currentEventUids.has(uid))
    const eventsForApi = events.map((eventType) => ({
      ...eventType,
      apiParam: 'event-type',
    }))

    const requestBody = {
      mentorSlug,
      events: eventsForApi,
      deleteEventUids: deleteEventUids.length > 0 ? deleteEventUids : undefined,
      ...(note && { note }),
    }

    Object.keys(requestBody).forEach((key) => {
      if (requestBody[key] === undefined) {
        delete requestBody[key]
      }
    })

    const resp = await postSPDataWithParams('/api/secure/cc/support/update/events/v2', requestBody, {})

    if (resp?.status === 200 && resp?.data?.code === 2000) {
      if (resp?.data?.data?.currentVersion != null) {
        setCurrentVersion(resp.data.data.currentVersion)
      }
      const refreshedMentor = await getMinimalMentorData(mentorSlug, true)
      if (refreshedMentor) {
        setMentor(refreshedMentor)
        setCurrentVersion(refreshedMentor?.currentVersion ?? refreshedMentor?.version ?? currentVersion)
        setOriginalEvents(Array.isArray(refreshedMentor?.eventTypes) ? [...refreshedMentor.eventTypes] : [])
      }
      await loadWorkspaceData()
      return true
    }

    toast.error(resp?.data?.message || 'Could not save event changes right now.')
    return false
  }

  async function revertEventTypes(targetVersion = null, note = '') {
    setIsReverting(true)
    try {
      const requestBody = {
        mentorSlug,
        ...(targetVersion !== null && { targetVersion }),
        ...(note && { note }),
      }

      const resp = await postSPDataWithParams(
        '/api/secure/cc/support/update/events/revert',
        requestBody,
        {},
      )

      if (resp?.status === 200 && resp?.data?.code === 2000) {
        if (resp?.data?.data?.currentVersion != null) {
          setCurrentVersion(resp.data.data.currentVersion)
        }
        const refreshedMentor = await getMinimalMentorData(mentorSlug, true)
        if (refreshedMentor) {
          setMentor(refreshedMentor)
          setCurrentVersion(refreshedMentor?.currentVersion ?? refreshedMentor?.version ?? currentVersion)
          setOriginalEvents(Array.isArray(refreshedMentor?.eventTypes) ? [...refreshedMentor.eventTypes] : [])
        }
        toast.success(
          `Successfully reverted to version ${
            resp?.data?.data?.restoredFromVersion || 'previous'
          }`,
        )
        await loadWorkspaceData()
        return true
      }

      toast.error(resp?.data?.message || 'Could not revert event configuration.')
      return false
    } catch (error) {
      toast.error('An error occurred while reverting the event configuration.')
      return false
    } finally {
      setIsReverting(false)
    }
  }

  const handleRevert = async (targetVersion, note) => {
    await revertEventTypes(targetVersion, note)
  }

  const closeEventTypeDrawer = useCallback(() => {
    setShowEventTypeModal(false)
    setSelectedEventType(null)
  }, [])

  const setShowEventTypeModalSafe = useCallback(
    (open) => {
      if (open === false) {
        closeEventTypeDrawer()
      } else {
        setShowEventTypeModal(true)
      }
    },
    [closeEventTypeDrawer],
  )

  const openNewEventDrawer = useCallback(() => {
    if (!selectedClinicAssociation) {
      return
    }
    setSelectedEventType({
      contextId: selectedClinicAssociation.id,
      clinicBrandId: selectedClinicAssociation.clinicBrandId,
    })
    setShowEventTypeModal(true)
  }, [selectedClinicAssociation])

  const handleEventCreation = useCallback(
    async (eventData) => {
      const nextEventData = { ...eventData }
      if (nextEventData?.contextId) {
        nextEventData.clinicBrandId = getClinicBrandIdFromClinicId(
          associatedClinics,
          nextEventData?.contextId,
        )
      } else {
        nextEventData.clinicBrandId = ''
      }

      const previousEvents = mentor?.eventTypes || []
      let nextEvents = [...previousEvents]
      if (nextEventData?.uid) {
        const eventIndex = nextEvents.findIndex((eventType) => eventType?.uid === nextEventData.uid)
        if (eventIndex >= 0) {
          nextEvents[eventIndex] = nextEventData
        }
      } else {
        nextEvents = [...nextEvents, nextEventData]
      }

      const note = nextEventData?.uid
        ? `Updated event: ${nextEventData?.title || nextEventData?.uid}`
        : `Created new event: ${nextEventData?.title || 'Untitled'}`

      const success = await saveEventTypes(nextEvents, note)
      if (success) {
        closeEventTypeDrawer()
      }
    },
    [associatedClinics, closeEventTypeDrawer, mentor?.eventTypes],
  )

  const openBundleEditor = useCallback(
    (bundlePack = null, clinicLocationId = selectedClinicAssociation?.id) => {
      setBundleEditorClinicId(clinicLocationId || '')
      if (clinicLocationId) {
        setSelectedClinicId(clinicLocationId)
      }
      setSelectedBundlePack(bundlePack)
      setShowBundleEditor(true)
    },
    [selectedClinicAssociation?.id],
  )

  const closeBundleEditor = useCallback(() => {
    setSelectedBundlePack(null)
    setBundleEditorClinicId('')
    setShowBundleEditor(false)
  }, [])

  const openNewBundlePackFlow = useCallback(() => {
    const initialClinicId =
      packClinicFilterId !== 'all'
        ? packClinicFilterId
        : selectedClinicAssociation?.id || associatedClinics?.[0]?.id || ''
    const initialClinic =
      associatedClinics.find((clinic) => clinic?.id === initialClinicId) || associatedClinics?.[0] || null
    setPendingBundleBrandId(initialClinic?.clinicBrandId || '')
    setPendingBundleClinicId(initialClinicId)
    setShowBundleScopePicker(true)
  }, [associatedClinics, packClinicFilterId, selectedClinicAssociation?.id])

  const closeBundleScopePicker = useCallback(() => {
    setShowBundleScopePicker(false)
    setPendingBundleBrandId('')
    setPendingBundleClinicId('')
  }, [])

  const confirmBundleScopePicker = useCallback(() => {
    if (!pendingBundleClinicId) {
      toast.error('Choose a clinic brand and location before creating a pack plan.')
      return
    }
    closeBundleScopePicker()
    openBundleEditor(null, pendingBundleClinicId)
  }, [closeBundleScopePicker, openBundleEditor, pendingBundleClinicId])

  const saveBundlePack = useCallback(
    async (bundlePackPayload) =>
      postSPDataWithParams(
        '/api/secure/cc/support/bundle-pack/save',
        bundlePackPayload,
        bundleEditorScopeRequestParams || {},
      ),
    [bundleEditorScopeRequestParams],
  )

  const handleTabChange = useCallback(
    (_, nextTab) => {
      navigate(`${canonicalBasePath}?tab=${nextTab}`)
    },
    [canonicalBasePath, navigate],
  )

  const handleRowAction = useCallback(
    (row) => {
      if (row?.rowKind === 'bundle-pack') {
        openBundleEditor(row?.raw || null, row?.clinicLocationId || selectedClinicAssociation?.id)
        return
      }
      if (row?.rowKind === 'event') {
        setSelectedEventType(row?.raw || null)
        setShowEventTypeModal(true)
        return
      }
      if (row?.rowKind === 'clinic-appointment' && selectedClinicAssociation?.slug) {
        navigate(
          `/support-home/clinics-hospitals/clinics/${selectedClinicAssociation.slug}/services`,
        )
      }
    },
    [navigate, openBundleEditor, selectedClinicAssociation?.id, selectedClinicAssociation?.slug],
  )

  if (isLoadingContext) {
    return (
      <>
        <SupportHeader heading="Practitioner Events & Plans" showBackButton />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
          <CircularProgress />
        </Box>
      </>
    )
  }

  return (
    <>
      <SupportHeader heading={pageHeading} subHeading={pageSubHeading} showBackButton>
        <Stack direction="row" spacing={1} alignItems="center">
          {currentVersion !== null ? (
            <Tooltip title={`Current Event Version: ${currentVersion}`}>
              <Chip
                icon={<History sx={{ fontSize: '16px !important' }} />}
                label={`v${currentVersion}`}
                variant="outlined"
                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.35)' }}
              />
            </Tooltip>
          ) : null}
          <Tooltip title="Revert event configuration">
            <span>
              <IconButton
                onClick={() => setShowRevertDialog(true)}
                disabled={!mentor}
                sx={{
                  color: 'rgba(255,255,255,0.92) !important',
                  border: '1px solid rgba(255,255,255,0.25)',
                  bgcolor: 'rgba(255,255,255,0.08)',
                }}
              >
                <Undo fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </SupportHeader>

      <Box sx={{ p: { xs: 2, md: 3 }, background: '#f7f8fb', minHeight: 'calc(100vh - 140px)' }}>
        <Stack spacing={3}>
          {pageError ? <Alert severity="error">{pageError}</Alert> : null}

          <Card>
            <CardContent>
              <Stack spacing={3}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ md: 'center' }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Clinic Scope
                    </Typography>
                    <Typography color="text.secondary">
                      {activeTab === WORKSPACE_TABS.packs
                        ? 'Choose the default clinic for overview and events. Pack plans below list all clinic scopes and can be filtered separately.'
                        : 'Choose the clinic where you want to review personal events, clinic appointments, and pack plans together.'}
                    </Typography>
                  </Box>
                  <FormControl sx={{ minWidth: { xs: '100%', md: 380 } }} size="small">
                    <InputLabel id="practitioner-workspace-clinic-label">Clinic</InputLabel>
                    <Select
                      labelId="practitioner-workspace-clinic-label"
                      label="Clinic"
                      value={selectedClinicId}
                      onChange={(event) => setSelectedClinicId(event.target.value)}
                    >
                      {associatedClinics.map((clinic) => (
                        <MenuItem key={clinic?.id} value={clinic?.id}>
                          {(clinic?.clinicBrandName || clinic?.clinicBrandSlug || 'Clinic brand') +
                            ' - ' +
                            (clinic?.title || clinic?.name || 'Clinic')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                {selectedClinicAssociation ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Person color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Practitioner</Typography>
                          <Typography sx={{ fontWeight: 700 }}>{mentor?.name || 'Unknown'}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Business color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Clinic Brand</Typography>
                          <Typography sx={{ fontWeight: 700 }}>
                            {selectedClinicAssociation?.clinicBrandName || selectedClinicAssociation?.clinicBrandSlug || 'Unknown'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Place color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Clinic Location</Typography>
                          <Typography sx={{ fontWeight: 700 }}>
                            {selectedClinicAssociation?.title || selectedClinicAssociation?.name || 'Unknown'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>
                ) : (
                  <Alert severity="warning">
                    This practitioner does not have a clinic scope selected yet.
                  </Alert>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">Personal Events</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>{practitionerEventRows.length}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">Clinic Appointments</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>{clinicAppointmentRows.length}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {activeTab === WORKSPACE_TABS.packs ? 'Pack Plans (All Clinics)' : 'Pack Plans'}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {activeTab === WORKSPACE_TABS.packs
                          ? allBundlePackRows.length
                          : selectedClinicBundlePackRows.length}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant={isMobile ? 'scrollable' : 'standard'}
                  allowScrollButtonsMobile
                >
                  {TAB_SEQUENCE.map((tabConfig) => (
                    <Tab key={tabConfig.id} label={tabConfig.label} value={tabConfig.id} />
                  ))}
                </Tabs>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Stack spacing={2.5}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ md: 'center' }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {currentTabConfig?.label}
                    </Typography>
                    <Typography color="text.secondary">
                      {currentTabConfig?.description}
                    </Typography>
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    {activeTab !== WORKSPACE_TABS.packs ? (
                      <Button
                        variant={activeTab === WORKSPACE_TABS.events ? 'contained' : 'outlined'}
                        startIcon={<Event />}
                        onClick={openNewEventDrawer}
                        disabled={!selectedClinicAssociation}
                      >
                        Create New Event
                      </Button>
                    ) : null}
                    {activeTab !== WORKSPACE_TABS.events ? (
                      <Button
                        variant={activeTab === WORKSPACE_TABS.packs ? 'contained' : 'outlined'}
                        startIcon={<LocalOffer />}
                        onClick={openNewBundlePackFlow}
                        disabled={associatedClinics.length === 0}
                      >
                        Create New Plan
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>

                {activeTab === WORKSPACE_TABS.packs ? (
                  <FormControl size="small" sx={{ width: { xs: '100%', md: 360 } }}>
                    <InputLabel id="pack-clinic-filter-label">Filter By Clinic</InputLabel>
                    <Select
                      labelId="pack-clinic-filter-label"
                      label="Filter By Clinic"
                      value={packClinicFilterId}
                      onChange={(event) => setPackClinicFilterId(event.target.value)}
                    >
                      <MenuItem value="all">All Clinics</MenuItem>
                      {associatedClinics.map((clinic) => (
                        <MenuItem key={clinic?.id} value={clinic?.id}>
                          {buildClinicScopeLabel(
                            clinic?.clinicBrandName || clinic?.clinicBrandSlug,
                            clinic?.title || clinic?.name,
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null}

                <EventTypeFilterControls
                  filterOptions={filterOptions}
                  selectedFilters={selectedFilters}
                  setSelectedFilters={setSelectedFilters}
                  showMixedFilter={showMixedFilter}
                  isMobile={isMobile}
                />

                {isLoadingWorkspaceData ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                  </Box>
                ) : visibleRows.length === 0 ? (
                  <Alert severity="info">
                    {activeTab === WORKSPACE_TABS.packs
                      ? 'No pack plans matched the selected clinic filter and event-type filters.'
                      : 'No entries matched the current clinic scope and event-type filters.'}
                  </Alert>
                ) : isMobile ? (
                  <WorkspaceRowsCards rows={visibleRows} onRowAction={handleRowAction} />
                ) : (
                  <WorkspaceRowsTable rows={visibleRows} onRowAction={handleRowAction} />
                )}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>

      <EventTypeFormDrawer
        open={showEventTypeModal}
        onClose={closeEventTypeDrawer}
        title={selectedEventType?.uid ? 'Edit Event' : 'Create Event'}
        subtitle="Configure session details, pricing, location, and clinic association for this practitioner."
      >
        {showEventTypeModal && mentor ? (
          <EventTypeCreationForm
            key={selectedEventType?.uid || selectedEventType?.id || selectedClinicAssociation?.id || 'new-event'}
            eventFormModalData={selectedEventType}
            setEventFormModalOpen={setShowEventTypeModalSafe}
            displayNamesOfEventTypes={displayNamesOfEventTypes}
            handleEventCreation={handleEventCreation}
            mentorClinicAssociationData={associatedClinics}
            mentorProfileData={mentor}
            oneOnOnePlans={oneOnOnePlans}
            bundlePacks={selectedScopeBundlePacks}
            refreshBundlePacks={loadWorkspaceData}
            refreshOneOnOnePlans={loadWorkspaceData}
            forceShowTherapistEventTypes
            saveOneOnOnePlanApi={`/api/secure/cc/support/1on1-custom-plan?mentor-id=${mentor?.id}`}
            showNextVisitSlotSelectorEnabler
            layout="drawer"
          />
        ) : null}
      </EventTypeFormDrawer>

      <Dialog
        open={showBundleEditor}
        onClose={closeBundleEditor}
        maxWidth="md"
        fullWidth
      >
        {showBundleEditor ? (
          <BaseAddCustomPriceComponent
            selectedPlan={selectedBundlePack}
            setSelectedPlan={setSelectedBundlePack}
            closeFunction={closeBundleEditor}
            refreshOneOnOnePlans={loadWorkspaceData}
            mentorProfileData={{
              ...mentor,
              location: {
                countryCode:
                  bundleEditorClinicAssociation?.countryCode ||
                  bundleEditorClinicAssociation?.location?.countryCode ||
                  mentor?.location?.countryCode,
              },
            }}
            type="bundle"
            bundleEditorMode="legacy"
            bundlePlanOptions={bundleEditorScopeSourcePlans.map((sourcePlan) => ({
              ...sourcePlan,
              id: sourcePlan?.pricingPackId || sourcePlan?.id,
              title: sourcePlan?.pricingPlanTitle || sourcePlan?.title,
            }))}
            saveBundlePackRequestFn={saveBundlePack}
            onSaveSuccess={async () => {
              await loadWorkspaceData()
            }}
          />
        ) : null}
      </Dialog>

      <Dialog open={showBundleScopePicker} onClose={closeBundleScopePicker} maxWidth="sm" fullWidth>
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Choose Clinic Brand And Location
              </Typography>
              <Typography color="text.secondary">
                Start by choosing the clinic brand and the clinic location. The pack editor will then show only the pricing plans that belong to that exact clinic scope.
              </Typography>
            </Box>
            <FormControl fullWidth size="small">
              <InputLabel id="new-pack-brand-label">Clinic Brand</InputLabel>
              <Select
                labelId="new-pack-brand-label"
                label="Clinic Brand"
                value={pendingBundleBrandId}
                onChange={(event) => setPendingBundleBrandId(event.target.value)}
              >
                {associatedClinicBrands.map((brand) => (
                  <MenuItem key={brand?.id} value={brand?.id}>
                    {brand?.title || 'Clinic brand'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="new-pack-clinic-scope-label">Clinic Location</InputLabel>
              <Select
                labelId="new-pack-clinic-scope-label"
                label="Clinic Location"
                value={pendingBundleClinicId}
                onChange={(event) => setPendingBundleClinicId(event.target.value)}
              >
                {pendingBundleBrandClinics.map((clinic) => (
                  <MenuItem key={clinic?.id} value={clinic?.id}>
                    {buildClinicScopeLabel(
                      clinic?.clinicBrandName || clinic?.clinicBrandSlug,
                      clinic?.title || clinic?.name,
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="outlined" onClick={closeBundleScopePicker}>
                Cancel
              </Button>
              <Button variant="contained" onClick={confirmBundleScopePicker}>
                Continue
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Dialog>

      <RevertEventsDialog
        open={showRevertDialog}
        onClose={() => setShowRevertDialog(false)}
        onRevert={handleRevert}
        currentVersion={currentVersion}
        isLoading={isReverting}
      />
    </>
  )
}
